/**
 * Every word the panel says to a shopper, in each language a shop can be read in.
 *
 * The language is the shop page's. The card script reads it from the page
 * (<html lang="ar">, on our storefronts and on Shopify alike) and passes it in
 * the panel's address as `lang`, because the panel lives on our own domain and
 * cannot read the shop's page for itself. Opened on its own, with no shop
 * around it, the panel speaks English.
 *
 * The card's own words live in sdk/src/utils/card-text.js; the two are kept in
 * the same plain modern Arabic so both read naturally in Egypt and Saudi Arabia.
 */
import { format } from "date-fns";

export const ARABIC = "ar";
export const ENGLISH = "en";
const LANG_PARAM = "lang";

// Arabic counts change their word with the number: one, two, three to ten,
// and eleven upwards each read differently.
const ARABIC_DUAL = 2;
const ARABIC_FEW_MAX = 10;

const TEXT = {
  en: {
    chat: "Chat",
    rewards: "Rewards",
    chatOrRewards: "Chat or rewards",
    backToChatServices: "Back to Chat Services",
    close: "Close",
    backToShop: "Back to the shop",
    askAnything: "Ask anything",
    talkToAgent: "Talk to the agent",
    voice: "Voice",
    readAloud: "Read answers aloud",
    stopReadingAloud: "Stop reading answers aloud",
    wait: "Wait",
    send: "Send",
    today: "Today",
    yesterday: "Yesterday",
    assistant: "Assistant",
    chatConversation: "Chat conversation",
    welcomeFallback: "Welcome! How can we help you?",
    signInRemember: "Sign in and I'll remember what you like.",
    loadingOlder: "Loading older messages...",
    scrollToBottom: "Scroll to bottom",
    showing: "Showing {name}",
    foundOne: "I found 1 result",
    foundMany: "I found {count} results",
    addAppleWallet: "Add to Apple Wallet",
    addGoogleWallet: "Add to Google Wallet",
    viewQr: "View QR Code",
    isTyping: "{name} is typing...",
    someoneTyping: "Someone is typing...",
    twoTyping: "2 people are typing...",
    manyTyping: "{count} people are typing...",
    connectingAgent: "Connecting you to an agent...",
    noAgents: "No agents available right now",
    backToAi: "Go back to AI",
    you: "You",
    failedLoadMessages: "Failed to load messages. Please try again.",
    failedSend: "Failed to send message. Please try again.",
    userMissing: "User information is missing. Please refresh the page.",
    voiceConnecting: "Connecting...",
    voiceListening: "Listening...",
    voiceThinking: "Thinking...",
    voiceProcessing: "Processing...",
    voiceSpeaking: "Speaking...",
    voiceSpeakingInterrupt: "Speaking - tap to interrupt",
    voicePaused: "Paused - tap mic to talk",
    voiceReconnecting: "Reconnecting...",
    voiceUnavailable: "Voice unavailable - tap mic to try again",
    voiceNotSupported: "Voice not supported",
    tapMicToSpeak: "Tap mic to speak",
    switchToEnglish: "Switch to English",
    switchToArabic: "Switch to Arabic",
    stopSpeaking: "Stop speaking",
    stopListening: "Stop listening",
    pauseListening: "Pause listening",
    startListening: "Start listening",
    endVoiceMode: "End voice mode",
    end: "End",
    guest: "Guest",
    user: "User",
    tapToLogin: "Tap to login to your account",
    minimizeChat: "Minimize Chat",
    hiName: "Hi {name},",
    hiThere: "Hi there,",
    howCanWeHelp: "How can we help you?",
    askAgentAnything: "Ask the agent anything...",
    askAgentAnythingLabel: "Ask the agent anything",
    sendMessage: "Send message",
    latestChat: "Latest chat",
    suggestedQuestions: "Suggested questions",
    menu: "Menu",
    signInEarnCoins: "Sign in to earn coins and keep your rewards",
    tasks: "Tasks",
    tasksHint: "Earn coins by finished tasks",
    referral: "Referral",
    referralHint: "Invite friends and earn coins",
    events: "Events",
    eventsHint: "Join brand events and earn coins",
    myProfile: "My Profile",
    signIn: "Sign in",
    groups: "Groups",
    wallet: "Wallet",
    chatSections: "Chat sections",
    poweredBy: "Powered by",
    aiAssistant: "AI Assistant",
    ourTeam: "Our Team",
    hereToHelp: "We are here to help!",
    somethingWrong: "Something went wrong, please try again.",
    pleaseSignIn: "Please sign in to continue",
  },
  ar: {
    chat: "المحادثة",
    rewards: "المكافآت",
    chatOrRewards: "المحادثة أو المكافآت",
    backToChatServices: "العودة",
    close: "إغلاق",
    backToShop: "العودة إلى المتجر",
    askAnything: "اسأل عن أي شيء",
    talkToAgent: "تحدّث مع المساعد",
    voice: "صوت",
    readAloud: "قراءة الإجابات بصوت عالٍ",
    stopReadingAloud: "إيقاف قراءة الإجابات",
    wait: "انتظر",
    send: "إرسال",
    today: "اليوم",
    yesterday: "أمس",
    assistant: "المساعد",
    chatConversation: "المحادثة",
    welcomeFallback: "أهلاً بك! كيف يمكننا مساعدتك؟",
    signInRemember: "سجّل الدخول وسأتذكّر ما يعجبك.",
    loadingOlder: "جارٍ تحميل الرسائل السابقة...",
    scrollToBottom: "الانتقال إلى الأسفل",
    showing: "تشاهد الآن {name}",
    foundOne: "وجدت نتيجة واحدة",
    foundTwo: "وجدت نتيجتين",
    foundFew: "وجدت {count} نتائج",
    foundMany: "وجدت {count} نتيجة",
    addAppleWallet: "أضف إلى Apple Wallet",
    addGoogleWallet: "أضف إلى Google Wallet",
    viewQr: "عرض رمز QR",
    isTyping: "{name} يكتب...",
    someoneTyping: "شخص ما يكتب...",
    twoTyping: "شخصان يكتبان...",
    manyTyping: "{count} أشخاص يكتبون...",
    connectingAgent: "جارٍ توصيلك بأحد الموظفين...",
    noAgents: "لا يوجد موظفون متاحون الآن",
    backToAi: "العودة إلى المساعد الذكي",
    you: "أنت",
    failedLoadMessages: "تعذّر تحميل الرسائل. حاول مرة أخرى.",
    failedSend: "تعذّر إرسال الرسالة. حاول مرة أخرى.",
    userMissing: "بيانات المستخدم غير متوفرة. أعد تحميل الصفحة.",
    voiceConnecting: "جارٍ الاتصال...",
    voiceListening: "أستمع إليك...",
    voiceThinking: "أفكّر...",
    voiceProcessing: "جارٍ المعالجة...",
    voiceSpeaking: "أتحدّث...",
    voiceSpeakingInterrupt: "أتحدّث - اضغط للمقاطعة",
    voicePaused: "متوقف مؤقتاً - اضغط على الميكروفون للتحدّث",
    voiceReconnecting: "جارٍ إعادة الاتصال...",
    voiceUnavailable: "الصوت غير متاح - اضغط على الميكروفون للمحاولة مجدداً",
    voiceNotSupported: "الصوت غير مدعوم",
    tapMicToSpeak: "اضغط على الميكروفون للتحدّث",
    switchToEnglish: "التبديل إلى الإنجليزية",
    switchToArabic: "التبديل إلى العربية",
    stopSpeaking: "إيقاف التحدّث",
    stopListening: "إيقاف الاستماع",
    pauseListening: "إيقاف الاستماع مؤقتاً",
    startListening: "بدء الاستماع",
    endVoiceMode: "إنهاء المحادثة الصوتية",
    end: "إنهاء",
    guest: "زائر",
    user: "مستخدم",
    tapToLogin: "اضغط لتسجيل الدخول إلى حسابك",
    minimizeChat: "تصغير المحادثة",
    hiName: "مرحباً {name}،",
    hiThere: "مرحباً،",
    howCanWeHelp: "كيف يمكننا مساعدتك؟",
    askAgentAnything: "اسأل المساعد عن أي شيء...",
    askAgentAnythingLabel: "اسأل المساعد عن أي شيء",
    sendMessage: "إرسال الرسالة",
    latestChat: "آخر محادثة",
    suggestedQuestions: "أسئلة مقترحة",
    menu: "القائمة",
    signInEarnCoins: "سجّل الدخول لتكسب العملات وتحتفظ بمكافآتك",
    tasks: "المهام",
    tasksHint: "اكسب العملات بإنجاز المهام",
    referral: "دعوة الأصدقاء",
    referralHint: "ادعُ أصدقاءك واكسب العملات",
    events: "الفعاليات",
    eventsHint: "شارك في فعاليات العلامة التجارية واكسب العملات",
    myProfile: "ملفي الشخصي",
    signIn: "تسجيل الدخول",
    groups: "المجموعات",
    wallet: "المحفظة",
    chatSections: "أقسام المحادثة",
    poweredBy: "مدعوم من",
    aiAssistant: "المساعد الذكي",
    ourTeam: "فريقنا",
    hereToHelp: "نحن هنا لمساعدتك!",
    somethingWrong: "حدث خطأ ما، حاول مرة أخرى.",
    pleaseSignIn: "سجّل الدخول للمتابعة",
  },
};

let cachedLang = null;

/** The shop page's language, as the card script passed it: "ar" or "en". */
export function panelLang() {
  if (cachedLang) return cachedLang;
  let lang = "";
  try {
    lang = new URLSearchParams(window.location.search).get(LANG_PARAM) || "";
  } catch (e) {
    lang = "";
  }
  cachedLang = lang.toLowerCase().split("-")[0] === ARABIC ? ARABIC : ENGLISH;
  return cachedLang;
}

export function isArabicPanel() {
  return panelLang() === ARABIC;
}

/** Forget the language read so far; tests change the address between cases. */
export function resetPanelLang() {
  cachedLang = null;
}

/**
 * A word the panel says, with {placeholders} filled in. English when a
 * language lacks the word, so a missing translation shows English, not nothing.
 */
export function pt(key, vars) {
  const table = TEXT[panelLang()];
  let text = (table && table[key]) || TEXT[ENGLISH][key] || "";
  if (vars) {
    Object.keys(vars).forEach((name) => {
      text = text.split(`{${name}}`).join(String(vars[name]));
    });
  }
  return text;
}

/** "I found N results", counted the way the language counts. */
export function foundLabel(count) {
  if (count === 1) return pt("foundOne");
  if (isArabicPanel()) {
    if (count === ARABIC_DUAL) return pt("foundTwo");
    if (count <= ARABIC_FEW_MAX) return pt("foundFew", { count });
  }
  return pt("foundMany", { count });
}

// Arabic dates come from the browser's own Arabic calendar text, with the
// Western digits the shop's prices already use.
const ARABIC_DATES = "ar-u-nu-latn";

/** A message's time: "9:48 PM", or "9:48 م" on an Arabic page. */
export function timeLabel(date) {
  if (!isArabicPanel()) return format(date, "p");
  return new Intl.DateTimeFormat(ARABIC_DATES, { hour: "numeric", minute: "2-digit" }).format(date);
}

/** A day older than yesterday: "Monday 13 October", in the panel's language. */
export function dayNameLabel(date) {
  if (!isArabicPanel()) return format(date, "EEEE d MMMM");
  return new Intl.DateTimeFormat(ARABIC_DATES, { weekday: "long", day: "numeric", month: "long" }).format(date);
}

/** Puts the whole panel in the page's language and reading direction. */
export function applyPanelLanguage() {
  const root = document.documentElement;
  root.lang = panelLang();
  root.dir = isArabicPanel() ? "rtl" : "ltr";
}
